<template lang='pug'>
.workout-statistics#WS-workout-statistics(:class="{'dark-mode': isDarkMode}")
  loading#WS-workout-statistics-loading(v-if="isLoading")
  .stats#WS-workout-statistics-content(v-else)
    Row
      Column(desktop="6" mobile="12")
        RogueSelect#WS-equipment-category-filter(:options="equipmentCategoryOptions" v-model="selectedCategory")
    .dates#WS-workout-filters(:class="{ rhino: isRhino }")
      Row(alignItems="flex-end")
        Column(desktop="3" mobile="6" v-if="!isRhino")
          RogueSelect#WS-equipment-filter(:label="filterByEquipmentLabel" :options="equipmentOptions" v-model="selectedEquipment")
        //- The design gives the rhino's Time Range the full width on a phone, with no equipment filter beside it
        Column(desktop="3" :mobile="isRhino ? '12' : '6'")
          RogueSelect#WS-time-range-filter(:label="timeRangeLabel" :options="timeRangeOptions" v-model="selectedTimeRange")
        Column.date-wrapper(desktop="3" mobile="12" v-if="selectedTimeRange === 'custom'")
          Field(:label="selectDateRangeLabel")
            .custom-date#WS-custom-date-trigger(:class="{active: showCalendar}" @click="showCalendar = !showCalendar" ref="date-range-trigger")
              | {{ rangeSelectorText }}
      .datepicker-wrapper#WS-datepicker-wrapper(v-if="showCalendar")
        .datepicker
          VueDatePicker#WS-date-picker(
            ref="date-range"
            v-model="dateRange"
            range
            inline
            :time-config="{ enableTimePicker: false }"
            auto-apply
            :year-range="yearRange"
            :max-date="today"
            :min-date="minDate"
            model-type="timestamp"
          )
        .datepicker-buttons
          RogueButton#WS-datepicker-cancel-button(:text="cancelButtonText" size="s" color="text-bold" @click="dateRange = previousDateRange; showCalendar = false;")
          RogueButton#WS-datepicker-reset-button(:text="resetButtonText" size="s" color="text-bold" :disabled="!dateRange" @click="dateRange = null")
          RogueButton#WS-datepicker-done-button(:text="doneButtonText" size="s" color="black" @click="showCalendar = false; applyFilters()")
    Row(v-if="showUnitSwitcher")
      Column(desktop="6" mobile="12")
        Field(v-if="isRhino" :label="displayWeightInLabel")
          RadioButtonGroup#WS-weight-unit-switcher(v-model="selectedWeightUnits" :options="weightUnitOptions" theme="pill-outline")
        Field(v-else :label="displayDistanceInLabel")
          RadioButtonGroup#WS-unit-switcher(v-model="selectedUnits" :options="unitOptions" theme="pill-outline")
    //- Air Rhino: weight, time, weight-seconds and reps, then workouts, calories and rope distance
    template(v-if="isRhino")
      Row#WS-rhino-stat-blocks-row
        Column(desktop="3" tablet="6" mobile="6")
          StatBlock#WS-stat-total-weight(:data="formattedRhinoWeight" :label="totalWeightLabel" testId="WS-stat-total-weight")
        Column(desktop="3" tablet="6" mobile="6")
          StatBlock#WS-stat-total-time(:data="formattedDuration" :label="totalTimeLabel" testId="WS-stat-total-time")
        Column(desktop="3" tablet="6" mobile="6")
          StatBlock#WS-stat-total-impulse(:data="formattedRhinoImpulse" :label="totalImpulseLabel" testId="WS-stat-total-impulse")
        Column(desktop="3" tablet="6" mobile="6")
          StatBlock#WS-stat-total-reps(:data="formattedRhinoReps" :label="totalRepsLabel" testId="WS-stat-total-reps")
      Row#WS-rhino-secondary-stat-blocks-row
        Column(desktop="4" tablet="4" mobile="4")
          StatBlock#WS-stat-workouts(:data="formattedCount" :label="workoutsLabel" testId="WS-stat-workouts")
        Column(desktop="4" tablet="4" mobile="4")
          StatBlock#WS-stat-calories(:data="formattedCalories" :label="caloriesLabel" testId="WS-stat-calories")
        Column(desktop="4" tablet="4" mobile="4")
          StatBlock#WS-stat-total-distance(:data="formattedRhinoDistance" :label="rhinoDistanceLabel" testId="WS-stat-total-distance")
    Row#WS-stat-blocks-row(v-else)
      Column(desktop="3" tablet="6" mobile="6")
        StatBlock#WS-stat-total-distance(:data="formattedDistance" :label="totalDistanceLabel" testId="WS-stat-total-distance")
      Column(desktop="3" tablet="6" mobile="6")
        StatBlock#WS-stat-total-time(:data="formattedDuration" :label="totalTimeLabel" testId="WS-stat-total-time")
      Column(desktop="3" tablet="6" mobile="6")
        StatBlock#WS-stat-workouts(:data="formattedCount" :label="workoutsLabel" testId="WS-stat-workouts")
      Column(desktop="3" tablet="6" mobile="6")
        StatBlock#WS-stat-calories(:data="formattedCalories" :label="caloriesLabel" testId="WS-stat-calories")
    Row#WS-bar-chart-row(v-if="barChartData")
      Column(desktop="12")
        h5.chart-header#WS-bar-chart-header {{ barChartHeader }}
        .no-workouts#WS-no-workouts-message(v-if="!filteredWorkouts.length")
          .bars-wrapper
            .bars
          .message {{ noWorkoutsMessage }}
        .bar-chart-wrapper#WS-bar-chart-wrapper(v-else)
          Bar.bar-chart#WS-bar-chart(:data="barChartData" :options="barChartOptions")
    Row#WS-pie-chart-row(v-if="!isRhino && selectedEquipment === 'all' && filteredWorkouts.length")
      Column(desktop="12")
        h5.chart-header#WS-pie-chart-header {{ pieChartHeader }}
        .pie-chart-wrapper#WS-pie-chart-wrapper
          Pie.pie-chart#WS-pie-chart(:data="pieChartData" :options="pieChartOptions")
        table.workout-stats-table.no-desc#WS-device-stats-table(v-if="deviceWorkoutSummaries.length")
          thead
            tr
              th {{ typeLabel }}
              th {{ distanceLabel }}
              th {{ avgSessionLabel }}
          tbody
            tr(v-for="(device, index) in deviceWorkoutSummaries" :id="`WS-device-stats-row-${index}`")
              td(:id="`WS-device-type-${index}`") {{ device.formattedType }}

              td(v-if="selectedUnits === 'meters'" :id="`WS-device-distance-${index}`") {{ `${device.distanceMeters}M`}}
              td(v-else-if="selectedUnits === 'miles'" :id="`WS-device-distance-${index}`") {{ `${device.distanceMiles}MI`}}
              td(v-else :id="`WS-device-distance-${index}`") {{ `${device.distanceYards}YDS`}}

              td(v-if="selectedUnits === 'meters'" :id="`WS-device-avg-${index}`") {{ `${device.avgMetersPerSession}M`}}
              td(v-else-if="selectedUnits === 'miles'" :id="`WS-device-avg-${index}`") {{ `${device.avgMilesPerSession}MI`}}
              td(v-else :id="`WS-device-avg-${index}`") {{ `${device.avgYardsPerSession}YDS`}}
    Row#WS-yearly-stats-row(v-if="!isRhino && selectedTimeRange === 'all' && filteredWorkouts.length")
      Column(desktop="12")
        h5.chart-header#WS-yearly-stats-header {{ statsByYearLabel }}
        table.workout-stats-table.no-desc#WS-yearly-stats-table
          thead
            tr
              th {{ yearLabel }}
              th {{ totalDistanceHeaderLabel }}
              th {{ viewLabel }}
          tbody
            tr(v-for="(data, index) in yearlyDistances" :id="`WS-yearly-stats-row-${index}`")
              td(:id="`WS-yearly-year-${index}`") {{ data.year }}
              td(:id="`WS-yearly-distance-${index}`") {{ data.distance }}
              td.has-button
                RogueButton(:text="viewStatsButtonText" size="xs" @click="selectedYear = data.year" :id="`WS-yearly-view-button-${index}`")
  Drawer#WS-year-stats-drawer(v-if="selectedYear" :title="yearStatsTitle" @hide="selectedYear = null")
    WorkoutStatsByYear#WS-workout-stats-by-year(:year="selectedYear" :device="selectedEquipment" :units="selectedUnits" :workouts="filteredWorkouts")
</template>
<script lang="ts" setup>
import { computed, onMounted, ref, toRaw, useTemplateRef, watch } from 'vue'
import { Bar, Pie } from 'vue-chartjs'
import {
  Chart as ChartJS,
  type ChartData,
  type ChartOptions,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
  ArcElement,
} from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { VueDatePicker } from '@vuepic/vue-datepicker'
import { useMQ } from 'sisu/js/mq'
import { useRoute } from 'vue-router'
import StatBlock from './StatBlock.vue'
import WorkoutStatsByYear from './WorkoutStatsByYear.vue'
import type { AggregatedWorkoutData, DistanceUnitsFilter, EquipmentCategoryFilter, EquipmentFilter, TimeRangeFilter, WeightUnitsFilter, Workout, WorkoutResponse } from './WorkoutTypes'
import { CUSTOM_DEVICE_MAP, FEET_PER_METER, RHINO, YARDS_PER_METER, formatElapsedTime, getStatsByYear, getWorkoutsByDevice, isMachineWorkout, isRhinoWorkout, mapBarChartData, mapPieChartData, mapRhinoBarChartData, rhinoImpulse, rhinoWeight, workoutShare } from './workout-helpers'
import type { WorkoutShare } from './workout-helpers'
import { useRogue } from '@/js/services/Rogue'
import '@vuepic/vue-datepicker/dist/main.css'

ChartJS.register(Tooltip, Legend, BarElement, CategoryScale, LinearScale, ArcElement, ChartDataLabels)

/**
 * Standalone, the page fetches the customer's workouts itself. Embedded where
 * they are already loaded (the Rogue Move calendar's Stats & History panel),
 * it is handed them instead, and reads a copy so its own bookkeeping never
 * touches the caller's records.
 */
const props = withDefaults(defineProps<{
  preloadedWorkouts?: Workout[] | null
}>(), {
  preloadedWorkouts: null,
})

const rogue = useRogue()
const route = useRoute()

const showUnitSwitcher: boolean = !rogue.isApp()
const isDarkMode = computed<boolean>(() => route.query.theme === 'dark')

const filterByEquipmentLabel = rogue.__('Filter By Equipment Type')
const timeRangeLabel = rogue.__('Time Range')
const selectDateRangeLabel = rogue.__('Select Date Range')
const displayDistanceInLabel = rogue.__('Display Distance In')
const displayWeightInLabel = rogue.__('Display Weight In')
const totalTimeLabel = rogue.__('Total Time')
const totalRepsLabel = rogue.__('Total Reps')
const workoutsLabel = rogue.__('Workouts')
const caloriesLabel = rogue.__('Calories')
const noWorkoutsMessage = rogue.__('No logged workouts for selected time period. Please  select a different time range.')
const statsByYearLabel = rogue.__('Stats By Year')
const yearLabel = rogue.__('Year')
const totalDistanceHeaderLabel = rogue.__('Total Distance')
const viewLabel = rogue.__('View')
const viewStatsButtonText = rogue.__('View Stats')
const typeLabel = rogue.__('Type')
const distanceLabel = rogue.__('Distance')
const avgSessionLabel = rogue.__('Avg/Session')
const cancelButtonText = rogue.__('Cancel')
const resetButtonText = rogue.__('Reset')
const doneButtonText = rogue.__('Done')

const barChartData = ref<ChartData | null>(null)
const pieChartData = ref<ChartData | null>(null)
const deviceWorkoutSummaries = ref<AggregatedWorkoutData[]>([])
const yearlyDistances = ref<{ [year: number]: string }[]>([])
const isLoading = ref(true)
const selectedYear = ref<number | null>(null)

const minDate = new Date(2025, 0, 1)
const today = new Date()
const yearRange: [number, number] = [minDate.getFullYear(), today.getFullYear()]

const rangeSelector = useTemplateRef<HTMLDivElement>('date-range')
const rangeSelectorText = computed<string>(() => (dateRange.value && dateRange.value[0] && dateRange.value[1])
  ? `${new Date(dateRange.value[0]).toLocaleDateString()} - ${new Date(dateRange.value[1]).toLocaleDateString()}`
  : rogue.__('Select'))
const dateRangeTrigger = useTemplateRef<HTMLDivElement>('date-range-trigger')
const mq = useMQ()

// The page's top split: the cardio machines share one view, the Air Rhino has its own
const selectedCategory = ref<EquipmentCategoryFilter['value']>('machines')
const equipmentCategoryOptions: EquipmentCategoryFilter[] = [
  {
    label : 'Bike, Ski, & Rower',
    value : 'machines',
  },
  {
    label : 'Air Rhino',
    value : 'rhino',
  },
]
const isRhino = computed<boolean>(() => selectedCategory.value === 'rhino')

const selectedEquipment = ref<EquipmentFilter['value']>('all')
const equipmentOptions = ref<EquipmentFilter[]>([
  {
    label : 'All',
    value : 'all',
  },
])

const dateRange = ref<[number, number] | null>(null)
const showCalendar = ref(false)
const previousDateRange = ref<[number, number] | null>(null)

const selectedTimeRange = ref<TimeRangeFilter['value']>('ytd')
const timeRangeOptions: TimeRangeFilter[] = [
  {
    label : 'Last Week',
    value : '1w',
  },
  {
    label : 'Last 4 Weeks',
    value : '4w',
  },
  {
    label : 'Last 3 Months',
    value : '3m',
  },
  {
    label : 'Year to Date',
    value : 'ytd',
  },
  {
    label : 'All Time',
    value : 'all',
  },
  {
    label : 'Custom Range',
    value : 'custom',
  },
]

// route.query.unit can be imperial or metric. Default to metric if not set.
const selectedUnits = ref<DistanceUnitsFilter['value']>(route.query.unit === 'imperial' ? 'miles' : 'meters')
const unitOptions: DistanceUnitsFilter[] = [
  {
    label : 'Meters',
    value : 'meters',
  },
  {
    label : 'Miles',
    value : 'miles',
  },
]

// The Air Rhino's weight unit follows the same metric/imperial setting the app hands over
const selectedWeightUnits = ref<WeightUnitsFilter['value']>(route.query.unit === 'imperial' ? 'lb' : 'kg')
const weightUnitOptions: WeightUnitsFilter[] = [
  {
    label : 'Kilograms',
    value : 'kg',
  },
  {
    label : 'Pounds',
    value : 'lb',
  },
]
const weightUnitLabel = computed<string>(() => selectedWeightUnits.value.toUpperCase())

const formattedUnits = computed<string>(() => isRhino.value
  ? `${weightUnitLabel.value}s`
  : selectedUnits.value.charAt(0).toUpperCase() + selectedUnits.value.slice(1).toLowerCase())
const formattedTimeRange = computed<string>(() => {
  switch (selectedTimeRange.value) {
    case '1w':
      return 'Day'
    case '4w':
      return 'Last 4 Weeks'
    case '3m':
    case 'ytd':
      return 'Month'
    case 'all':
    default:
      return 'Year'
  }
})
const barChartHeader = computed<string>(() => {
  if (selectedTimeRange.value === 'custom') {
    return previousDateRange.value
      ? `${formattedUnits.value} By: ${new Date(previousDateRange.value[0]).toLocaleDateString()} - ${new Date(previousDateRange.value[1]).toLocaleDateString()}`
      : `${formattedUnits.value} By: Date Range Unspecified`
  }

  return `${formattedUnits.value} By ${formattedTimeRange.value}`
})
const pieChartHeader = computed<string>(() => `${formattedUnits.value} By Type`)

const yearStatsTitle = computed<string>(() => {
  const title = `${selectedYear.value} STATS`

  if (selectedEquipment.value !== 'all') {
    return `${title} - ${CUSTOM_DEVICE_MAP[selectedEquipment.value].label.toUpperCase()}`
  }

  return title
})

const workouts = ref<Workout[]>([])
const filteredWorkouts = ref<Workout[]>([])

// Each category reads only the records its machines took part in; a session that mixed the Air Rhino
// with a bike belongs to both, and each view reads its own machines' share of it
const categoryWorkouts = computed<Workout[]>(() => workouts.value.filter(workout => isRhino.value ? isRhinoWorkout(workout) : isMachineWorkout(workout)))

function applyFilters(): void {
  const now = Date.now()
  const currentDate = new Date()
  const threeMonthsDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 2, 1)

  // User selected custom date range but hasn't selected a range yet
  if (selectedTimeRange.value === 'custom' && !dateRange.value) {
    filteredWorkouts.value = []
  } else {
    let customEndTime = now
    let customStartTime = now
    if (selectedTimeRange.value === 'custom' && dateRange.value) {
      if (!dateRange.value[1]) dateRange.value = [dateRange.value[0], now]

      const startDate = new Date(dateRange.value[0])
      startDate.setHours(0, 0, 0, 0)
      customStartTime = startDate.getTime()

      const endDate = new Date(dateRange.value[1])
      endDate.setHours(23, 59, 59, 999)
      customEndTime = endDate.getTime()
    }

    const startThresholds: { [key: string]: number } = {
      '1w'     : now - 7 * 24 * 60 * 60 * 1000,
      '4w'     : now - 28 * 24 * 60 * 60 * 1000,
      '3m'     : threeMonthsDate.getTime(),
      'ytd'    : new Date(currentDate.getFullYear(), 0, 1).getTime(),
      'custom' : customStartTime,
    }

    filteredWorkouts.value = categoryWorkouts.value
      .filter(workout => {
        if (!workout.data.performanceMetrics) return false
        if (isRhino.value || selectedEquipment.value === 'all') return true
        if (workout.data.performanceMetrics.byModality) {
          return selectedEquipment.value in workout.data.performanceMetrics.byModality
        }
        if (workout.data.multiMachineIntervalResults) {
          return Object.values(workout.data.multiMachineIntervalResults).flat().some(r => r.machineRole === selectedEquipment.value)
        }

        return selectedEquipment.value === workout.data.deviceInfo?.deviceType
      })
      .filter(workout => {
        // If the start time wasn't provided, we can only include this workout under "All Time"
        if (!workout.data.payload?.createdAt) return selectedTimeRange.value === 'all'

        const startTime = new Date(workout.data.payload?.createdAt).getTime()

        if (selectedTimeRange.value in startThresholds) {
          return startTime >= startThresholds[selectedTimeRange.value]
        }

        return true
      })
      .filter(workout => {
        // All ranges except custom don't have an end time
        if (selectedTimeRange.value !== 'custom') return true

        // If the start time wasn't provided, we don't know if it is in the cutom range
        if (!workout.data.payload?.createdAt) return false

        const startTime = new Date(workout.data.payload?.createdAt).getTime()

        return startTime <= customEndTime
      })
  }

  mapCharts()
}

/** Redraws the charts from the filtered records; the machines' pie and tables stay as they were when the rhino shows */
function mapCharts(): void {
  if (isRhino.value) {
    barChartData.value = mapRhinoBarChartData(filteredWorkouts.value, selectedTimeRange.value, selectedWeightUnits.value, dateRange.value)

    return
  }
  barChartData.value = mapBarChartData(filteredWorkouts.value, selectedTimeRange.value, selectedUnits.value, dateRange.value, selectedEquipment.value)
  pieChartData.value = mapPieChartData(filteredWorkouts.value, selectedUnits.value, selectedEquipment.value)
  deviceWorkoutSummaries.value = getWorkoutsByDevice(filteredWorkouts.value, selectedEquipment.value)
  yearlyDistances.value = getStatsByYear(filteredWorkouts.value, selectedUnits.value, selectedEquipment.value)
}

// Each record's share of the view: the Air Rhino's, one machine's, or every cardio machine's
const viewEquipment = computed<string>(() => isRhino.value ? RHINO : selectedEquipment.value)
const shares = computed(() => filteredWorkouts.value.map(workout => workoutShare(workout, viewEquipment.value)))
const sumShares = (read: (share: WorkoutShare) => number): number => shares.value.reduce((sum, share) => sum + read(share), 0)

const totalDistance = computed<number>(() => sumShares(share => share.distance))
const totalDistanceYards = computed<number>(() => totalDistance.value * YARDS_PER_METER)
const formattedDistance = computed<string>(() => {
  if (!totalDistance.value) return '—'

  switch (selectedUnits.value) {
    case 'yards':
      return Math.round(totalDistanceYards.value).toLocaleString()
    case 'miles':
      return parseFloat((totalDistanceYards.value / 1760).toFixed(2)).toLocaleString()
    case 'meters':
    default:
      return Math.round(totalDistance.value).toLocaleString()
  }
})

const totalDistanceLabel = computed<string>(() => {
  switch (selectedUnits.value) {
    case 'miles':
      return rogue.__('Total Miles')
    case 'yards':
      return rogue.__('Total Yards')
    case 'meters':
    default:
      return rogue.__('Total Meters')
  }
})

const totalDurationMs = computed<number>(() => sumShares(share => share.durationMs))

const formattedDuration = computed<string>(() => {
  if (!totalDurationMs.value) return '—'

  return formatElapsedTime(totalDurationMs.value)
})

const workoutCount = computed<number>(() => filteredWorkouts.value.length)
const formattedCount = computed<string>(() => workoutCount.value ? workoutCount.value.toLocaleString() : '—')

const totalCalories = computed<number>(() => sumShares(share => share.calories))
const formattedCalories = computed<string>(() => totalCalories.value ? totalCalories.value.toLocaleString() : '—')

// Air Rhino totals, each in the chosen weight unit whatever unit the console recorded in
const sumRhino = (read: (workout: Workout) => number): number => filteredWorkouts.value.reduce((sum, workout) => sum + read(workout), 0)
const totalRhinoWeight = computed<number>(() => sumRhino(workout => rhinoWeight(workout.data.performanceMetrics, selectedWeightUnits.value)))
const totalRhinoImpulse = computed<number>(() => sumRhino(workout => rhinoImpulse(workout.data.performanceMetrics, selectedWeightUnits.value)))
const totalRhinoReps = computed<number>(() => sumRhino(workout => workout.data.performanceMetrics?.totalReps || 0))

// The tiles carry the unit in their labels, as the design does, so the figures stay bare
const formattedRhinoWeight = computed<string>(() => totalRhinoWeight.value ? Math.round(totalRhinoWeight.value).toLocaleString() : '—')
const formattedRhinoImpulse = computed<string>(() => totalRhinoImpulse.value ? Math.round(totalRhinoImpulse.value).toLocaleString() : '—')
const formattedRhinoReps = computed<string>(() => totalRhinoReps.value ? totalRhinoReps.value.toLocaleString() : '—')
// The rhino's distance is its rope travel: meters, or feet under the imperial setting
const formattedRhinoDistance = computed<string>(() => {
  if (!totalDistance.value) return '—'

  return selectedWeightUnits.value === 'lb'
    ? Math.round(totalDistance.value * FEET_PER_METER).toLocaleString()
    : Math.round(totalDistance.value).toLocaleString()
})

const totalWeightLabel = computed<string>(() => selectedWeightUnits.value === 'lb' ? rogue.__('Total LBs') : rogue.__('Total KGs'))
const totalImpulseLabel = computed<string>(() => selectedWeightUnits.value === 'lb' ? rogue.__('Total LB-S') : rogue.__('Total KG-S'))
const rhinoDistanceLabel = computed<string>(() => selectedWeightUnits.value === 'lb' ? rogue.__('Total Feet') : rogue.__('Total Meters'))

function updateEquipmentOptions() {
  equipmentOptions.value = [
    {
      label : 'All',
      value : 'all',
    },
  ]

  // The Air Rhino is never a key here, so its records add nothing unless a cardio machine shared the session
  Object.keys(CUSTOM_DEVICE_MAP).forEach(key => {
    if (key !== 'unknown' && workouts.value.some(w => {
      if (w.data.performanceMetrics?.byModality) {
        return key in w.data.performanceMetrics.byModality
      }
      if (w.data.multiMachineIntervalResults) {
        return Object.values(w.data.multiMachineIntervalResults).flat().some(r => r.machineRole === key)
      }

      return w.data.deviceInfo?.deviceType === key
    })) {
      equipmentOptions.value.push({
        label : CUSTOM_DEVICE_MAP[key].label,
        value : key,
      })
    }
  })
}

async function load() {
  isLoading.value = true
  try {
    // Handed-in records arrive as Vue proxies, which structuredClone refuses; the raw objects copy
    workouts.value = props.preloadedWorkouts
      ? props.preloadedWorkouts.map(workout => structuredClone(toRaw(workout)))
      : (await rogue.fetch<WorkoutResponse>(`svc_move/site/${rogue.site.code}/workouts/performances`)).workouts
    updateEquipmentOptions()
    applyFilters()
  } catch (e) {
    rogue.log.error('Unable to load workout statistics for this customer', e as Error)
  }
  isLoading.value = false
}

const barChartOptions = computed<ChartOptions>(() => {
  const textColor = isDarkMode.value ? '#fff' : '#000'
  const gridColor = isDarkMode.value ? '#444' : '#E9E9E9'

  return {
    responsive          : true,
    maintainAspectRatio : false,
    scales              : {
      x: {
        stacked : true,
        border  : {
          display: false,
        },
        grid: {
          display: false,
        },
        ticks: {
          color : textColor,
          font  : {
            family : '"Roboto", Helvetica, Arial, sans-serif',
            size   : 12,
            weight : 'bold',
          },
        },
      },
      y: {
        stacked     : true,
        beginAtZero : true,
        position    : 'right',
        ticks       : {
          maxTicksLimit : 5,
          callback      : function (value: string | number) {
            const dist = value as number
            // The design's weight axis reads 1K, 2K ... with a bare 0 at the baseline
            if (isRhino.value) {
              return dist >= 1000
                ? `${parseFloat((dist / 1000).toFixed(2)).toLocaleString()}K`
                : Math.round(dist).toLocaleString()
            }
            switch (selectedUnits.value) {
              case 'yards':
                return `${Math.round(dist).toLocaleString()}YDS`
              case 'miles':
                return `${parseFloat(dist.toFixed(2)).toLocaleString()}MI`
              case 'meters':
              default:
                return dist >= 1000
                  ? `${parseFloat((dist / 1000).toFixed(2)).toLocaleString()}K`
                  : `${Math.round(dist).toLocaleString()}M`
            }
          },
          color : textColor,
          font  : {
            family : '"Roboto", Helvetica, Arial, sans-serif',
            size   : 12,
            weight : 'bold',
          },
        },
        border: {
          display: false,
        },
        grid: {
          color : gridColor,
          z     : 1,
        },
      },
    },
    plugins: {
      legend: {
        align    : 'center',
        position : 'bottom',
        reverse  : true,
        labels   : {
          boxWidth  : 10,
          boxHeight : 10,
          color     : textColor,
          font      : {
            family     : '"Roboto", Helvetica, Arial, sans-serif',
            lineHeight : 1,
            size       : 14,
          },
          padding       : 16,
          pointStyle    : 'circle',
          usePointStyle : true,
        },
      },
      datalabels: {
        display: false,
      },
    },
  }
})

const pieChartOptions = computed(() => {
  const textColor = isDarkMode.value ? '#fff' : '#000'

  return {
    responsive          : true,
    maintainAspectRatio : false,
    animation           : false,
    layout              : {
      padding: {
        left  : 0,
        right : 0,
      },
    },
    plugins: {
      legend: {
        align    : 'start',
        position : 'right',
        reverse  : true,
        labels   : {
          boxWidth  : 10,
          boxHeight : 10,
          color     : textColor,
          font      : {
            family     : '"Roboto", Helvetica, Arial, sans-serif',
            lineHeight : 1,
            size       : 14,
          },
          padding       : 16,
          pointStyle    : 'circle',
          usePointStyle : true,
        },
      },
      // Configuration for 'chartjs-plugin-datalabels' to display percentages inside the slices
      datalabels: {
        color : '#fff',
        font  : {
          weight : 'normal',
          family : '"Roboto", Helvetica, Arial, sans-serif',
          size   : 16,
        },
        formatter: (value: number, context: any) => {
          const total = context.chart.data.datasets[0].data.reduce((acc: any, val: any) => acc + (val as number), 0)
          const percentage = total > 0 ? (value / total * 100).toFixed(0) : '0'

          return `${percentage}%`
        },
        align  : 'start',
        anchor : 'end',
      } as any,
    },
  }
})

watch(showCalendar, () => {
  previousDateRange.value = dateRange.value
})
watch(selectedTimeRange, async (value, oldValue) => {
  if (value === 'custom' && oldValue !== 'custom') {
    showCalendar.value = true
  } else if ((value !== 'custom' && oldValue === 'custom')) {
    showCalendar.value = false
  }
  applyFilters()
})
watch(selectedEquipment, applyFilters)
watch(selectedCategory, () => {
  // The equipment filter belongs to the machines; the rhino view starts over from every rhino record
  selectedEquipment.value = 'all'
  applyFilters()
})
watch([selectedUnits, selectedWeightUnits], mapCharts)

onMounted(() => {
  load()
})

defineExpose({
  load,
})
</script>

<style lang="scss" scoped>
.workout-statistics {
  .dates {
    position: relative;
    z-index: 1;

    .custom-date {
      background: $color-gray-x-light;
      border-radius: $space-xxs;
      border: 1px solid $color-gray-x-light;
      padding: $space-xs;
      font-size: $size-xs;
      width: 100%;
      color: $color-gray-dark;
      cursor: pointer;

      &.active {
        border-color: $color-gray;
        color: $color-black;
      }
    }

    .datepicker-wrapper {
      margin-bottom: $space-m;
      background-color: $color-gray-x-light;
      border-radius: $space-xxxs;

      .datepicker {
        margin: 0;
      }

      .datepicker-buttons {
        border-top: 1px solid $color-gray;
        padding: $space-xs;
        display: flex;
        flex-direction: row;
        gap: $space-l;
        justify-content: right;

        @include not-mobile() {
          gap: $space-m;
          justify-content: space-between;
        }

        :deep(.button) {
          padding: $space-xs;
          min-height: auto;
          margin-bottom: 0;
          text-transform: capitalize;

          &:last-child {
            padding: $space-xs $space-xl;
            margin-bottom: $size-l;
            font-weight: normal;

            @include not-mobile() {
              margin-bottom: 0;
              padding: $space-xs $space-xs;
            }
          }
        }
      }

      @include not-mobile() {
        background-color: $color-gray-x-light;
        position: absolute;
        z-index: 1;
        border:1px solid $color-gray;
        left: 0;
        width: 265px;
        min-height: 380px;

        @include tablet() {
          top: 148px;
        }
        @include all-desktop() {
          left: calc(50% + 8px);
          top: 60px;
        }
      }
    }

    // The rhino view has no equipment filter, so its date field sits one column to the left
    &.rhino .datepicker-wrapper {
      @include all-desktop() {
        left: calc(25% + 8px);
      }
    }
  }

  .no-workouts {
    align-items: center;
    background-color: $color-gray-x-light;
    border-radius: $space-xxs;
    display: flex;
    flex-direction: column;
    gap: $space-m;
    justify-content: center;
    min-height: 183px;
    width: 100%;
    padding: $space-s;

    @keyframes pulse {
      0%, 100% {
        height: 34px;
      }
      50% {
        height: 15px;
      }
    }

    .bars-wrapper {
      height: 34px;
      display: flex;
      flex-direction: column;
      justify-content: flex-end;

      .bars {
        background: $color-gray-dark;
        width: 10px;
        height: 34px;
        margin: 0 auto;
        position: relative;
        animation: pulse 4s ease-in-out infinite;

        &::before,
        &::after {
          content: "";
          position: absolute;
          width: 10px;
          background: $color-gray-dark;
          bottom: 0;
        }

        &::before {
          height: 24px;
          left: -15px;
          animation: pulse 4s ease-in-out infinite;
          animation-delay: 1s;
        }

        &::after {
          height: 19px;
          right: -15px;
          animation: pulse 4s ease-in-out infinite;
          animation-delay: 2s;
        }
      }
    }

    .message {
      color: $color-gray-dark;
      font-size: $size-xs;
      height: 32px;
      text-align: center;
      max-width: 311px;
    }
  }

  .bar-chart {
    background-color: $color-gray-x-light;
    border-radius: $space-xxs;
    height: 250px;
    padding: $space-xs;
  }

  .pie-chart {
    height: 250px;
  }

  .chart-header {
    font-size: $size-s;
  }

  table.workout-stats-table {
    thead th {
      border: none;
      font-size: $size-xs;
      font-weight: normal;
      padding: $space-s;
      text-align: left;
      text-transform: none;

      &:last-of-type {
        text-align: right;
      }
    }
    tbody {
      tr {
        border: none;
        border-top: 1px solid transparent;

        &:first-of-type {
          td:first-of-type {
            border-top-left-radius: $space-xxs;
          }

          td:last-of-type {
            border-top-right-radius: $space-xxs;
          }
        }

        &:last-of-type {
          td:first-of-type {
            border-bottom-left-radius: $space-xxs;
          }

          td:last-of-type {
            border-bottom-right-radius: $space-xxs;
          }
        }

        td {
          border: none;
          border-top: 1px solid white;
          font-size: $size-xs;
          padding: $space-m $space-s;
          text-align: left;
          background-color: $color-gray-x-light;

          &.has-button {
            padding: $space-xs $space-s;
          }

          &:last-of-type {
            text-align: right;
            display: flex;
            flex-direction: row;
            justify-content: end;
          }
        }
      }
    }
  }
}

.workout-statistics :deep(.dp--theme-light) {
  /* Set background color of calendar */
  --dp-background-color: #F8F8F8;
  --dp-text-color: #000;
  --dp-hover-color: #E9E9E9;
  --dp-hover-text-color: #000;
  --dp-primary-color: #333333;
  --dp-border-color: #F8F8F8;
  --dp-menu-border-color: #F8F8F8;
  --dp-border-color-hover: #F8F8F8;
  --dp-border-color-focus: #F8F8F8;
  --dp-icon-color: #a2a2a2;
  --dp-font-family: 'Roboto', Helvetica, Arial, sans-serif;
  --dp-month-year-row-height: 45px;
  --dp-menu-padding: 16px 8px 8px 8px;
  --dp-primary-disabled-color: $color-gray-x-dark;
  --dp-cell-border-radius: 18px;
  --dp-secondary-color: #A2A2A2;
  --dp-range-between-dates-background-color: #d2d2d2;
  --dp-range-between-border-color: #d2d2d2;
}

:deep(.dp--calendar-header) {
  text-transform: uppercase;
  font-weight: normal;
}

:deep(.dp--month-year-wrap) {
  gap: $space-s;
}

:deep(.dp--calendar-header) {
  font-size: $size-xs;
  line-height: 35px;
}

:deep(button.dp--month-year-select) {
  background: rgb(233, 233, 233);
  border-radius: $space-xxs;
  font-size: $size-xs;
  padding: $space-s;
}

:deep(button.dp--month-year-select) {
  background: rgb(233, 233, 233);
  border-radius: $space-xxs;
  font-size: $size-xs;
  padding: $space-s;
}

:deep(.dp--flex-display) {
  @include mobile() {
    flex-direction: column;
  }
}

:deep(.dp--overlay-cell-disabled) {
  color: var(--dp-secondary-color);
}

:deep(.dp--overlay) {
  padding: $space-xs;
}

:deep(.dp--cell-inner) {
  min-width: 100%;
}

:deep(.dp--overlay-action) {
  max-width: calc(100% - 24px);
}

/* Dark mode styles */
.workout-statistics.dark-mode {
  color: $color-white;

  .dates {
    .custom-date {
      background: $color-black-light;
      border-color: $color-black-light;
      color: $color-gray-dark;

      &.active {
        border-color: $color-gray-dark;
        color: $color-white;
      }
    }

    .datepicker-wrapper {
      background-color: $color-black-light;
      border-color: $color-gray-x-dark;

      .datepicker-buttons {
        border-top-color: $color-gray-x-dark;

        :deep(.button) {
          padding: $space-xs;
          color: #fff;
          text-transform: capitalize;

          &:last-child {
            padding: $space-xs $space-xl;
            margin-bottom: $size-l;
            font-weight: normal;
            background: #444;
          }
        }
      }

      @include not-mobile() {
        background-color: $color-black-light;
        border-color: $color-gray-x-dark;
      }
    }
  }

  .no-workouts {
    background-color: $color-black-light;

    .message {
      color: $color-gray-dark;
    }
  }

  .bar-chart {
    background-color: $color-black-light;
  }

  .chart-header {
    color: $color-white;
  }

  table.workout-stats-table {
    thead th {
      color: $color-gray-dark;
    }
    tbody {
      tr {
        td {
          border-top-color: $color-black;
          background-color: $color-black-light;
          color: $color-white;
        }
      }
    }
  }

  :deep(.dp--theme-light) {
    --dp-background-color: #333333;
    --dp-text-color: #fff;
    --dp-hover-color: #444444;
    --dp-hover-text-color: #fff;
    --dp-primary-color: #666666;
    --dp-border-color: #333333;
    --dp-menu-border-color: #333333;
    --dp-border-color-hover: #333333;
    --dp-border-color-focus: #333333;
    --dp-icon-color: #a2a2a2;
    --dp-secondary-color: #666666;
    --dp-range-between-dates-background-color: #444444;
    --dp-range-between-border-color: #444444;
  }

  :deep(button.dp--month-year-select) {
    background: #444444;
    color: $color-white;
  }
}
</style>

<style lang="scss">
/* Increase label size for better readability in workout statistics */
.workout-statistics .field .label {
  font-size: $size-s !important;
}

@supports (-webkit-overflow-scrolling: touch) {
  .workout-statistics select:focus-visible {
    outline-offset: -3px;
  }
}
</style>
